import { useState } from "react";
import PropTypes from "prop-types";
import {
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import styles from "./accValidationModal.module.css";
import axios from "axios";
import Config from "../../service/Config";

/* helpers */
function fmtDate(d) {
  try {
    if (!d) return "—";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt?.getTime())) return "—";
    return dt.toLocaleString();
  } catch {
    return "—";
  }
}

const RowItem = ({ label, value }) => (
  <div className={styles.mobileMenuContainer}>
    <div className={styles.mobileMenuele1}>{label}</div>
    <div className={styles.mobileMenuele2}>{value ?? "—"}</div>
  </div>
);

const AccValidationModal = ({
  row,
  isVisible,
  onClose,
  className = "",
  setRefreshData,
}) => {
  console.log(row);

  const API_BASE =
    import.meta.env?.VITE_API_BASE_URL || "https://kyc.getcupi.com/api";

  // state (hooks must be at the top)
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  // reject dialog state
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  // bail out early (after hooks)
  if (!isVisible) return null;

  const r = row || {};
  console.log("r------>", r);

  const showrejectReason =
    r.review_reason ?? r.__raw?.review_reason ?? r.reason ?? "";
  const displayDate = r.date || fmtDate(r.created_at);

  // identify record
  const companyIdForPayload = r.company_id || r.__raw?.company_id;
  const accNoForPayload = r.acc_no || r.__raw?.acc_no;
  const beneficiary_name = r.beneficiary_name || r.__raw?.beneficiary_name;
  const ledger_label = r.ledger_label || r.__raw?.ledger_label;
  const ifsc = r.ifsc || r.__raw?.ifsc;
  const referenceIdForPayload = r.reference_id || r.__raw?.reference_id;

  // normalized final-state check to hide buttons when already decided
  const normalizedStatus = String(r.status || "").toLowerCase();
  const normalizedReview = String(r.review_status || "").toLowerCase();
  const isFinal =
    normalizedStatus === "approved" ||
    normalizedStatus === "rejected" ||
    normalizedReview === "approved" ||
    normalizedReview === "rejected";

  async function handleDecision(decision, reason) {
    setErrMsg("");
    if (!companyIdForPayload || !accNoForPayload) {
      setErrMsg("Missing identifiers to update this record.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${Config.kyc_api_endpoint}/bank-account/decision`,
        {
          company_id: companyIdForPayload,
          account_no: accNoForPayload, // ← backend expects `account_no`
          beneficiary_name: beneficiary_name,
          ledger_label: ledger_label,
          ifsc: ifsc,
          reference_id: referenceIdForPayload, // optional; backend can ignore
          status: decision, // "approved" | "rejected"
          reason: reason?.trim() || undefined, // backend can store as review_reason
        },
        {
          headers: {
            "X-API-Key": process.env.REACT_APP_CUPI_API_KEY, // Vite: import.meta.env.VITE_CUPI_API_KEY
            // "Content-Type": "application/json", // axios sets this automatically for JSON
          },
          validateStatus: () => true, // (optional) handle non-2xx yourself
        }
      );
      setSaving(false);
      setRejectOpen(false);
      setRejectReason("");
      setRefreshData(true);
      onClose();
    } catch (e) {
      setSaving(false);
      setErrMsg(e?.response?.data?.error || e.message || "Update failed");
    }
  }

  function openRejectDialog() {
    setReasonError("");
    setRejectReason("");
    setRejectOpen(true);
  }

  async function confirmReject() {
    const rsn = rejectReason.trim();
    if (rsn.length < 3) {
      setReasonError("Please provide a short reason (min 3 characters).");
      return;
    }
    await handleDecision("rejected", rsn);
  }
  console.log("row-->", row);

  return (
    <div
      className={styles.frameParent}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className={styles.frameGroup} onClick={onClose}>
        <img className={styles.arrowNarrowLeftIcon} alt="" src="/xclose.svg" />
      </div>

      <div className={styles.frameParent1}>
        <div className={styles.frameWrapperContainer}>
          <div className={styles.frameWrapper}>
            <div className={styles.mobileMenuParentHeading}>
              Account Details
            </div>

            <div className={styles.mobileMenuParent22}>
              <RowItem label="DATE" value={displayDate} />
              <RowItem
                label="COMPANY NAME"
                value={r.company_name || r.comp_name || "—"}
              />

              <RowItem label="ACCOUNT NO" value={r.acc_no} />
              <RowItem
                label="BENEFICIARY NAME"
                value={r.beneficiary_name || r.bene_name}
              />
              <RowItem label="IFSC" value={r.ifsc} />
            </div>
            {showrejectReason && (
              <>
                <hr style={{ marginTop: "20px" }} />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    padding: "12px",
                    gap: "6px",
                    maxWidth: "100%",
                    wordBreak: "break-word", // ✅ allow long reasons to wrap
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#B20A00",
                      fontWeight: 700,
                      fontSize: { xs: 18, sm: 22 },
                    }}
                  >
                    Rejected
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "#4C4E64",
                      fontWeight: 600,
                      fontSize: { xs: 14, sm: 16 },
                    }}
                  >
                    Reason for rejection
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "#4C4E64",
                      fontWeight: 600,
                      fontSize: { xs: 14, sm: 16 },
                      textAlign: "center",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {showrejectReason}
                  </Typography>
                </div>
              </>
            )}

            {errMsg && (
              <Typography sx={{ color: "#B00020", mt: 1, fontSize: 14 }}>
                {errMsg}
              </Typography>
            )}
          </div>

          <br />

          {!isFinal && (
            <Stack direction="row" spacing={5} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={
                  !saving && (
                    <img
                      src="/check.svg"
                      alt=""
                      style={{ width: 18, height: 18 }}
                    />
                  )
                }
                sx={{
                  backgroundColor: "#2348AB",
                  color: "#FFFFFF",
                  "&:hover": { backgroundColor: "#1d3a89" },
                  minWidth: 140,
                }}
                disabled={saving}
                onClick={() => handleDecision("approved")}
              >
                {saving ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Approve"
                )}
              </Button>

              <Button
                variant="contained"
                startIcon={
                  !saving && (
                    <img
                      src="/x.svg"
                      alt=""
                      style={{ width: 18, height: 18 }}
                    />
                  )
                }
                sx={{
                  backgroundColor: "#FFFFFF",
                  color: "#2348AB",
                  border: "1px solid #2348AB",
                  "&:hover": { backgroundColor: "#f5f7fb" },
                  minWidth: 140,
                }}
                disabled={saving}
                onClick={openRejectDialog}
              >
                {saving ? (
                  <CircularProgress size={20} sx={{ color: "#2348AB" }} />
                ) : (
                  "Reject"
                )}
              </Button>
            </Stack>
          )}
        </div>
      </div>

      {/* Reject Reason Dialog */}
      <Dialog
        open={rejectOpen}
        onClose={() => !saving && setRejectOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={{ borderRadius: "18px" }}
      >
        <DialogTitle sx={{ color: "#2348AB", fontWeight: 700 }}>
          Please provide a reason for rejection.
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            placeholder="Write reason for reject..."
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              if (reasonError) setReasonError("");
            }}
            error={!!reasonError}
            helperText={reasonError || " "}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRejectOpen(false)}
            variant="outlined"
            disabled={saving}
            sx={{ textTransform: "none", width: "120px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmReject}
            variant="contained"
            disabled={saving}
            sx={{
              textTransform: "none",
              backgroundColor: "#2348AB",
              "&:hover": { backgroundColor: "#1d3a89" },
              width: "120px",
            }}
          >
            {saving ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

AccValidationModal.propTypes = {
  row: PropTypes.object,
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  className: PropTypes.string,
  setRefreshData: PropTypes.func,
};

export default AccValidationModal;
